import type { LegalPage, LanguageKey } from "@akina/domain";
import { useDeveloperToolsStore, useCmsCopyStore } from "@akina/presentation";
import { useQuery } from "@tanstack/react-query";
import { getCmsContent, CMS_PATHS } from "../../adapters";
import { loggerService } from "../../observability";
import { QueryKeys } from "../QueryKeys";
import { useApi } from "../useApi";

export type LegalPageKey = LegalPage["key"];

export const useFetchLegalPage = (key: LegalPageKey, app: "care" | "portal" = "care",language?: LanguageKey, isDeepLinkHandled: boolean = true) => {
  const { api } = useApi();
  const getLegalPage = useCmsCopyStore(state => state.actions.getLegalPage);
  const cmsCopy = useCmsCopyStore(state => state.cmsCopy);
  const { useCmsPreview } = useDeveloperToolsStore();
  const effectiveLanguage = language ?? "en";

  return useQuery({
    queryKey: [QueryKeys.fetchLegalPage, key, effectiveLanguage],
    queryFn: async () => {
      const result = await getCmsContent<
        LegalPage | undefined,
        [LegalPageKey, "care" | "portal", LanguageKey | undefined]
      >(getLegalPage, api.legalPage.getLegalPage, useCmsPreview, key, app, effectiveLanguage);

      if (!result) {
        loggerService.error(
          new Error("No legal page content found"),
          JSON.stringify({
            key,
            effectiveLanguage,
            availableStories: Object.keys(cmsCopy!.otherStories).filter(k =>
              k.startsWith(CMS_PATHS.LEGAL_PAGE[app]),
            ),
          }),
          {
            level: "error",
          },
        );
        throw new Error(`No legal page content found for key: ${key} and language: ${effectiveLanguage}`);
      }

      return result;
    },
    enabled: isDeepLinkHandled && !!key && !!cmsCopy?.otherStories,
    retry: 3,
    retryDelay: 500,
  });
};
